import { type FormEvent, useCallback, useEffect, useState } from "react";
import { api, type RefundRecord } from "./api";

type Props = {
  restaurantId: string;
  onToast?: (t: { title: string; body: string; type: "success" | "error" | "info" }) => void;
};

const REASON_LABELS: Record<string, string> = {
  missing_items: "Missing items",
  wrong_items: "Wrong items",
  quality_issue: "Quality issue",
  late_delivery: "Late delivery",
  never_delivered: "Never delivered",
  customer_request: "Customer request",
  duplicate_order: "Duplicate order",
  other: "Other",
};

const STATUS_LABELS: Record<string, string> = {
  requested: "Requested",
  pending_provider: "Pending provider",
  approved: "Approved",
  rejected: "Rejected",
  escalated: "Escalated",
  settled: "Settled",
  cancelled: "Cancelled",
};

const STATUS_COLORS: Record<string, string> = {
  requested: "#3b82f6",
  pending_provider: "#f59e0b",
  approved: "#10b981",
  rejected: "#ef4444",
  escalated: "#8b5cf6",
  settled: "#6b7280",
  cancelled: "#9ca3af",
};

function formatMoney(minor: number | null | undefined): string {
  if (minor == null) return "0.00";
  return (minor / 100).toFixed(2);
}

const REFUND_FILTERS = ["all", "requested", "pending_provider", "approved", "rejected", "escalated", "settled", "cancelled"] as const;

export function RefundPanel({ restaurantId, onToast }: Props) {
  const [refunds, setRefunds] = useState<RefundRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form fields
  const [orderId, setOrderId] = useState("");
  const [reason, setReason] = useState("other");
  const [reasonDetails, setReasonDetails] = useState("");
  const [amount, setAmount] = useState("");

  const loadRefunds = useCallback(() => {
    setLoading(true);
    const statusParam = statusFilter === "all" ? undefined : statusFilter;
    api.listRefunds(restaurantId, statusParam)
      .then(setRefunds)
      .catch((err: Error) => {
        onToast?.({ title: "Error loading refunds", body: err.message, type: "error" });
      })
      .finally(() => setLoading(false));
  }, [restaurantId, statusFilter, onToast]);

  useEffect(() => { loadRefunds(); }, [loadRefunds]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!orderId || !amount) return;
    setSubmitting(true);
    try {
      const amountMinor = Math.round(parseFloat(amount) * 100);
      const refund = await api.createRefund({
        order_id: orderId,
        reason,
        reason_details: reasonDetails || undefined,
        requested_amount_minor: amountMinor,
      });
      setRefunds((prev) => [refund, ...prev]);
      setShowForm(false);
      setOrderId("");
      setReason("other");
      setReasonDetails("");
      setAmount("");
      onToast?.({ title: "Refund created", body: `#${refund.id.slice(0, 8)}`, type: "success" });
    } catch (err: unknown) {
      onToast?.({ title: "Error", body: err instanceof Error ? err.message : String(err), type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTransition = async (refundId: string, newStatus: string) => {
    try {
      const updated = await api.transitionRefund(refundId, { status: newStatus });
      setRefunds((prev) => prev.map((r) => (r.id === refundId ? updated : r)));
      onToast?.({ title: "Refund updated", body: `→ ${STATUS_LABELS[newStatus] ?? newStatus}`, type: "success" });
    } catch (err: unknown) {
      onToast?.({ title: "Error", body: err instanceof Error ? err.message : String(err), type: "error" });
    }
  };

  const nextActions: Record<string, string[]> = {
    requested: ["pending_provider", "approved", "rejected", "cancelled"],
    pending_provider: ["approved", "rejected", "escalated", "cancelled"],
    escalated: ["approved", "rejected", "settled"],
    approved: ["settled"],
  };

  return (
    <div style={{ padding: "0 0 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Refunds & Disputes</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ New Refund"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={{
          background: "var(--surface, #1e1e2e)", border: "1px solid var(--border, #333)",
          borderRadius: 8, padding: 16, marginBottom: 16, display: "flex", flexDirection: "column", gap: 12,
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, color: "#999" }}>Order ID</span>
              <input value={orderId} onChange={(e) => setOrderId(e.target.value)} required
                     placeholder="UUID of the order" style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid var(--border, #444)", background: "var(--bg, #111)", color: "inherit" }} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, color: "#999" }}>Amount</span>
              <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} required
                     placeholder="e.g. 12.99" style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid var(--border, #444)", background: "var(--bg, #111)", color: "inherit" }} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, color: "#999" }}>Reason</span>
              <select value={reason} onChange={(e) => setReason(e.target.value)}
                      style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid var(--border, #444)", background: "var(--bg, #111)", color: "inherit" }}>
                {Object.entries(REASON_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, color: "#999" }}>Details (optional)</span>
              <input value={reasonDetails} onChange={(e) => setReasonDetails(e.target.value)}
                     placeholder="Additional context" style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid var(--border, #444)", background: "var(--bg, #111)", color: "inherit" }} />
            </label>
          </div>
          <button className="btn btn-primary" type="submit" disabled={submitting} style={{ alignSelf: "flex-end" }}>
            {submitting ? "Creating…" : "Submit Refund"}
          </button>
        </form>
      )}

      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {REFUND_FILTERS.map((f) => (
          <button key={f} className={`btn btn-secondary-sm ${statusFilter === f ? "active" : ""}`}
                  style={{
                    padding: "4px 10px", fontSize: 12, borderRadius: 16,
                    border: `1px solid ${statusFilter === f ? "#3b82f6" : "var(--border, #444)"}`,
                    background: statusFilter === f ? "#3b82f633" : "transparent",
                    color: statusFilter === f ? "#3b82f6" : "inherit",
                    cursor: "pointer",
                  }}
                  onClick={() => setStatusFilter(f)}>
            {f === "all" ? "All" : STATUS_LABELS[f] ?? f}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="muted">Loading refunds…</p>
      ) : refunds.length === 0 ? (
        <p className="muted" style={{ textAlign: "center", padding: 24 }}>No refunds found.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {refunds.map((r) => (
            <div key={r.id} style={{
              background: "var(--surface, #1e1e2e)", border: "1px solid var(--border, #333)",
              borderRadius: 8, padding: 14,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div>
                  <span style={{
                    display: "inline-block", padding: "2px 8px", borderRadius: 12,
                    fontSize: 11, fontWeight: 600, color: "#fff",
                    background: STATUS_COLORS[r.status] ?? "#555",
                  }}>
                    {STATUS_LABELS[r.status] ?? r.status}
                  </span>
                  <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                    {r.platform.replace("_", " ")}
                  </span>
                </div>
                <span style={{ fontWeight: 600 }}>
                  {formatMoney(r.requested_amount_minor)} {r.currency}
                </span>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 13, flexWrap: "wrap" }}>
                <span className="muted">Reason: {REASON_LABELS[r.reason] ?? r.reason}</span>
                {r.reason_details && <span className="muted">{r.reason_details}</span>}
                <span className="muted">Order: {r.order_id.slice(0, 8)}…</span>
                <span className="muted">{new Date(r.requested_at).toLocaleString()}</span>
                {r.approved_amount_minor != null && (
                  <span style={{ color: "#10b981" }}>Approved: {formatMoney(r.approved_amount_minor)} {r.currency}</span>
                )}
                {r.resolved_at && (
                  <span className="muted">Resolved: {new Date(r.resolved_at).toLocaleString()}</span>
                )}
              </div>
              {nextActions[r.status] && (
                <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                  {nextActions[r.status].map((s) => (
                    <button key={s} className="btn btn-secondary-sm"
                            style={{
                              padding: "4px 10px", fontSize: 11, borderRadius: 6,
                              border: `1px solid ${STATUS_COLORS[s] ?? "var(--border)"}`,
                              color: STATUS_COLORS[s] ?? "inherit",
                              cursor: "pointer", background: "transparent",
                            }}
                            onClick={() => handleTransition(r.id, s)}>
                      → {STATUS_LABELS[s] ?? s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
